import { useMemo, useCallback, useState } from "react";

/**
 * Shared utilities for call action hooks
 */

/** Container class for call action UIs when used inside a call menu (no background — blends into parent) */
export const CALL_MENU_CONTAINER_CLASS = "space-y-4 mt-2 rounded-lg";

// Helper to get common styling values (used within hooks with useMemo)
const getCommonStylingValues = (context) => {
  const isIVR = context === 'ivrMenu' || context === 'offHoursIvrMenu' || context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours';
  
  return {
    containerClass: isIVR 
      ? "space-y-4 shadow-sm bg-gray-50 mt-2 rounded-lg"
      : "bg-white border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm",
    inputClass: isIVR
      ? "w-full bg-transparent border-b-[1px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      : "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
    headerSize: isIVR
      ? "font-semibold text-[15px]"
      : "font-bold text-[18px]",
    showDivider: isIVR,
    useIVRStyle: isIVR,
  };
};

// Options are stored as object keyed by DTMF digit { "1": {...}, "2": {...} }. Find option by id.
const getOptionFromOptions = (options, ivrOptionId) => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
  return Object.values(options).find((opt) => opt?.id === ivrOptionId) ?? null;
};

// Helper to get value from route based on context (optionKey when provided avoids lookup by id)
const getValueFromRoute = (context, route, ivrOptionId, fieldMap, optionKey = null) => {
  if (context === 'ivrMenu') {
    const option = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : getOptionFromOptions(route?.ivrMenu?.options, ivrOptionId);
    return option?.[fieldMap.ivr] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'ivrMenuOffHours') {
    const option = optionKey != null ? route?.ivrMenu?.options?.[optionKey] : getOptionFromOptions(route?.ivrMenu?.options, ivrOptionId);
    return option?.[fieldMap.offHours] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'offHoursIvrMenuOffHours') {
    const option = getOptionFromOptions(route?.offHoursIvrMenu?.options, ivrOptionId);
    return option?.[fieldMap.offHours] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'offHoursIvrMenu') {
    const option = getOptionFromOptions(route?.offHoursIvrMenu?.options, ivrOptionId);
    return option?.[fieldMap.ivr] ?? fieldMap.defaultValue ?? "";
  } else if (context === 'offHours') {
    return route?.[fieldMap.offHours] ?? fieldMap.defaultValue ?? "";
  } else {
    return route?.[fieldMap.root] ?? fieldMap.defaultValue ?? "";
  }
};

// Find option key ("1", "2", ...) by option id in options object
const getOptionKeyById = (options, ivrOptionId) => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
  const entry = Object.entries(options).find(([, opt]) => opt?.id === ivrOptionId);
  return entry ? entry[0] : null;
};

// Helper to update route based on context (options keyed by DTMF digit). optionKey when provided targets that key directly.
const updateRouteValue = (context, setRoute, ivrOptionId, fieldMap, value, additionalUpdates = {}, optionKey = null) => {
  if (context === 'ivrMenu') {
    setRoute((prev) => {
      const key = optionKey != null ? optionKey : getOptionKeyById(prev.ivrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.ivrMenu?.options ?? {};
      return {
        ...prev,
        ivrMenu: {
          ...prev.ivrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.ivr]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'ivrMenuOffHours') {
    setRoute((prev) => {
      const key = optionKey != null ? optionKey : getOptionKeyById(prev.ivrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.ivrMenu?.options ?? {};
      return {
        ...prev,
        ivrMenu: {
          ...prev.ivrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.offHours]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'offHoursIvrMenuOffHours') {
    setRoute((prev) => {
      const key = getOptionKeyById(prev.offHoursIvrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.offHoursIvrMenu?.options ?? {};
      return {
        ...prev,
        offHoursIvrMenu: {
          ...prev.offHoursIvrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.offHours]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'offHoursIvrMenu') {
    setRoute((prev) => {
      const key = getOptionKeyById(prev.offHoursIvrMenu?.options, ivrOptionId);
      if (key == null) return prev;
      const options = prev.offHoursIvrMenu?.options ?? {};
      return {
        ...prev,
        offHoursIvrMenu: {
          ...prev.offHoursIvrMenu,
          options: {
            ...options,
            [key]: {
              ...options[key],
              [fieldMap.ivr]: value,
              ...additionalUpdates,
            },
          },
        },
      };
    });
  } else if (context === 'offHours') {
    setRoute((prev) => ({
      ...prev,
      [fieldMap.offHours]: value,
      ...additionalUpdates,
    }));
  } else {
    setRoute((prev) => ({
      ...prev,
      [fieldMap.root]: value,
      ...additionalUpdates,
    }));
  }
};

/**
 * Shared hook for Forward action component logic across all contexts
 */
export const useForwardAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  // Get the current number value based on context (optionKey ensures we read from the correct option)
  const numberValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryNumber',
      offHours: 'offHoursNumber',
      root: 'primaryNumber',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Get the current caller ID preference value based on context (IVR uses "caller"/"twilio")
  const callerIdPref = useMemo(() => {
    const defaultValue = (context === 'ivrMenu' || context === 'ivrMenuOffHours' || context === 'offHoursIvrMenu' || context === 'offHoursIvrMenuOffHours') ? 'caller' : 'caller';
    const raw = getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'callerIdPref',
      offHours: 'offHoursCallerIdPref',
      root: 'callerIdPref',
      defaultValue,
    }, optionKey);
    // Map legacy values to caller/twilio
    if (raw === 'original' || raw === 'system') return raw === 'original' ? 'caller' : 'twilio';
    return raw || defaultValue;
  }, [context, route, ivrOptionId, optionKey]);

  // Handle number change (optionKey ensures we write to the correct option)
  const handleNumberChange = useCallback((value) => {
    let additionalUpdates = {};
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      additionalUpdates = { primaryAction: "forward" };
    } else if (context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      additionalUpdates = { offHoursAction: "forward" };
    }
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryNumber',
      offHours: 'offHoursNumber',
      root: 'primaryNumber',
    }, value, additionalUpdates, optionKey);

    // Clear channel for non-IVR contexts
    if (context === 'offHours') {
      setRoute((prev) => ({ ...prev, offHoursChannel: null }));
    } else if (context === 'root') {
      setRoute((prev) => ({ ...prev, primaryChannel: null }));
    }
  }, [context, setRoute, ivrOptionId, optionKey]);

  // Handle caller ID preference change
  const handleCallerIdChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'callerIdPref',
      offHours: 'offHoursCallerIdPref',
      root: 'callerIdPref',
    }, value, {}, optionKey);

    // Clear channel for non-IVR contexts
    if (context === 'offHours') {
      setRoute((prev) => ({ ...prev, offHoursChannel: null }));
    } else if (context === 'root') {
      setRoute((prev) => ({ ...prev, primaryChannel: null }));
    }
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  const callerIdOptions = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu' || context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      return [
        { value: "caller", label: "Show Original Caller's Number" },
        { value: "twilio", label: "Show Twilio Line Number" },
      ];
    }
    return null; // ForwardUI uses its default original/system
  }, [context]);

  return {
    numberValue,
    callerIdPref,
    handleNumberChange,
    handleCallerIdChange,
    callerIdOptions,
    ...styling,
  };
};

/**
 * Shared hook for ForwardVM action component logic across all contexts
 */
export const useForwardVMAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  // Get the current number value based on context
  const numberValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryNumber',
      offHours: 'offHoursNumber',
      root: 'primaryNumber',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Get the current caller ID preference value based on context
  const callerIdPref = useMemo(() => {
    let raw;
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      raw = getValueFromRoute(context, route, ivrOptionId, {
        ivr: 'callerIdPref',
        offHours: 'callerIdPref',
        root: 'callerIdPref',
        defaultValue: '',
      }, optionKey);
    } else if (context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      raw = getValueFromRoute(context, route, ivrOptionId, {
        ivr: 'callerIdPref',
        offHours: 'offHoursCallerIdPref',
        root: 'callerIdPref',
        defaultValue: '',
      }, optionKey);
    } else {
      raw = getValueFromRoute(context, route, ivrOptionId, {
        ivr: 'callerIdPref',
        offHours: 'offHoursCallerIdPref',
        root: 'callerIdPref',
        defaultValue: 'caller',
      }, optionKey);
    }
    // Map legacy values to caller/twilio
    if (raw === 'original' || raw === 'system') return raw === 'original' ? 'caller' : 'twilio';
    return raw || (context === 'root' || context === 'offHours' ? 'caller' : raw);
  }, [context, route, ivrOptionId, optionKey]);

  // Get call approval: per-option for IVR menu options, route-level otherwise. Option falls back to route default.
  const callApproval = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu' || context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      const optionValue = getValueFromRoute(context, route, ivrOptionId, {
        ivr: 'callApproval',
        offHours: 'callApproval',
        root: 'callApproval',
      }, optionKey);
      return optionValue || route?.callApproval || "disabled";
    }
    return route?.callApproval || "disabled";
  }, [context, route, ivrOptionId, optionKey]);

  // Get recording value based on context
  const recordingValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle number change
  const handleNumberChange = useCallback((value) => {
    let additionalUpdates = {};
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      additionalUpdates = { primaryAction: "forwardvwm" };
    } else if (context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      additionalUpdates = { offHoursAction: "forwardvwm" };
    }
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryNumber',
      offHours: 'offHoursNumber',
      root: 'primaryNumber',
    }, value, additionalUpdates, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  // Handle caller ID preference change
  const handleCallerIdChange = useCallback((value) => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: 'callerIdPref',
        offHours: 'callerIdPref',
        root: 'callerIdPref',
      }, value, {}, optionKey);
    } else if (context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: 'callerIdPref',
        offHours: 'offHoursCallerIdPref',
        root: 'callerIdPref',
      }, value, {}, optionKey);
    } else {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: 'callerIdPref',
        offHours: 'offHoursCallerIdPref',
        root: 'callerIdPref',
      }, value, {}, optionKey);
    }
  }, [context, setRoute, ivrOptionId, optionKey]);

  // Handle call approval change: per-option for IVR menu, route-level for root/offHours
  const handleCallApprovalChange = useCallback((value) => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu' || context === 'ivrMenuOffHours' || context === 'offHoursIvrMenuOffHours') {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: 'callApproval',
        offHours: 'callApproval',
        root: 'callApproval',
      }, value, {}, optionKey);
    } else {
      setRoute((prev) => ({
        ...prev,
        callApproval: value,
        ...(context === 'offHours' ? { offHoursChannel: null } : { primaryChannel: null }),
      }));
    }
  }, [context, setRoute, ivrOptionId, optionKey]);

  // Handle recording change
  const handleRecordingChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  // Determine caller ID options based on context (caller/twilio for storage)
  const callerIdOptions = useMemo(() => {
    return [
      { value: "caller", label: "Show Original Caller's Number" },
      { value: "twilio", label: "Show System Number" },
    ];
  }, []);

  return {
    numberValue,
    callerIdPref,
    callApproval,
    recordingValue,
    handleNumberChange,
    handleCallerIdChange,
    handleCallApprovalChange,
    handleRecordingChange,
    callerIdOptions,
    ...styling,
  };
};

export const DYNAMIC_FORWARD_BACKUP_NONE = "none";

export const DYNAMIC_FORWARD_PRIMARY_DEFAULTS = {
  twilanswerVmFallback: true,
  callerIdPref: "twilio",
  callApproval: "enabled",
};

export const DYNAMIC_FORWARD_OFF_HOURS_SLOT_DEFAULTS = {
  offHoursTwilanswerVmFallback: true,
  offHoursCallerIdPref: "twilio",
  callApproval: "enabled",
};

/** Fields to merge when selecting dynamic_forward in a route or IVR option */
export function getDynamicForwardInitForAction(action, isOffHoursSlot = false) {
  if (action !== "dynamic_forward") return {};
  return isOffHoursSlot
    ? { ...DYNAMIC_FORWARD_OFF_HOURS_SLOT_DEFAULTS }
    : { ...DYNAMIC_FORWARD_PRIMARY_DEFAULTS };
}

/**
 * Shared hook for Dynamic Forward action component logic across all contexts
 */
export const useDynamicForwardAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  const numberValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: "primaryNumber",
      offHours: "offHoursNumber",
      root: "primaryNumber",
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  const callerIdPref = useMemo(() => {
    let raw;
    if (context === "ivrMenu" || context === "offHoursIvrMenu") {
      raw = getValueFromRoute(context, route, ivrOptionId, {
        ivr: "callerIdPref",
        offHours: "callerIdPref",
        root: "callerIdPref",
        defaultValue: "twilio",
      }, optionKey);
    } else if (context === "ivrMenuOffHours" || context === "offHoursIvrMenuOffHours") {
      raw = getValueFromRoute(context, route, ivrOptionId, {
        ivr: "callerIdPref",
        offHours: "offHoursCallerIdPref",
        root: "callerIdPref",
        defaultValue: "twilio",
      }, optionKey);
    } else {
      raw = getValueFromRoute(context, route, ivrOptionId, {
        ivr: "callerIdPref",
        offHours: "offHoursCallerIdPref",
        root: "callerIdPref",
        defaultValue: "twilio",
      }, optionKey);
    }
    if (raw === "original" || raw === "system") return raw === "original" ? "caller" : "twilio";
    return raw || "twilio";
  }, [context, route, ivrOptionId, optionKey]);

  const callApproval = useMemo(() => {
    if (context === "ivrMenu" || context === "offHoursIvrMenu" || context === "ivrMenuOffHours" || context === "offHoursIvrMenuOffHours") {
      const optionValue = getValueFromRoute(context, route, ivrOptionId, {
        ivr: "callApproval",
        offHours: "callApproval",
        root: "callApproval",
      }, optionKey);
      return optionValue || route?.callApproval || "enabled";
    }
    return route?.callApproval || "enabled";
  }, [context, route, ivrOptionId, optionKey]);

  const recordingValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: "primaryRecording",
      offHours: "offHoursRecording",
      root: "primaryRecording",
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  const twilanswerVmFallback = useMemo(() => {
    const raw = getValueFromRoute(context, route, ivrOptionId, {
      ivr: "twilanswerVmFallback",
      offHours: "offHoursTwilanswerVmFallback",
      root: "twilanswerVmFallback",
      defaultValue: true,
    }, optionKey);
    return raw === true || raw?.BOOL === true;
  }, [context, route, ivrOptionId, optionKey]);

  const handleNumberChange = useCallback((value) => {
    let additionalUpdates = {};
    if (context === "ivrMenu" || context === "offHoursIvrMenu") {
      additionalUpdates = { primaryAction: "dynamic_forward" };
    } else if (context === "ivrMenuOffHours" || context === "offHoursIvrMenuOffHours") {
      additionalUpdates = { offHoursAction: "dynamic_forward" };
    }
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: "primaryNumber",
      offHours: "offHoursNumber",
      root: "primaryNumber",
    }, value, additionalUpdates, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const handleCallerIdChange = useCallback((value) => {
    if (context === "ivrMenu" || context === "offHoursIvrMenu") {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: "callerIdPref",
        offHours: "callerIdPref",
        root: "callerIdPref",
      }, value, {}, optionKey);
    } else if (context === "ivrMenuOffHours" || context === "offHoursIvrMenuOffHours") {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: "callerIdPref",
        offHours: "offHoursCallerIdPref",
        root: "callerIdPref",
      }, value, {}, optionKey);
    } else {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: "callerIdPref",
        offHours: "offHoursCallerIdPref",
        root: "callerIdPref",
      }, value, {}, optionKey);
    }
  }, [context, setRoute, ivrOptionId, optionKey]);

  const handleCallApprovalChange = useCallback((value) => {
    if (context === "ivrMenu" || context === "offHoursIvrMenu" || context === "ivrMenuOffHours" || context === "offHoursIvrMenuOffHours") {
      updateRouteValue(context, setRoute, ivrOptionId, {
        ivr: "callApproval",
        offHours: "callApproval",
        root: "callApproval",
      }, value, {}, optionKey);
    } else {
      setRoute((prev) => ({
        ...prev,
        callApproval: value,
        ...(context === "offHours" ? { offHoursChannel: null } : { primaryChannel: null }),
      }));
    }
  }, [context, setRoute, ivrOptionId, optionKey]);

  const handleRecordingChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: "primaryRecording",
      offHours: "offHoursRecording",
      root: "primaryRecording",
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const handleTwilanswerVmFallbackChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: "twilanswerVmFallback",
      offHours: "offHoursTwilanswerVmFallback",
      root: "twilanswerVmFallback",
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  const callerIdOptions = useMemo(() => {
    return [
      { value: "caller", label: "Show Original Caller's Number" },
      { value: "twilio", label: "Show System Number" },
    ];
  }, []);

  return {
    numberValue,
    callerIdPref,
    callApproval,
    recordingValue,
    twilanswerVmFallback,
    handleNumberChange,
    handleCallerIdChange,
    handleCallApprovalChange,
    handleRecordingChange,
    handleTwilanswerVmFallbackChange,
    callerIdOptions,
    ...styling,
  };
};

/**
 * Shared hook for SendToVoicemail action component logic across all contexts
 */
export const useSendToVoicemailAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  // Get recording value based on context
  const recordingValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle recording change
  const handleRecordingChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  // SendToVoicemail has slightly different styling - empty container for IVR
  const containerClass = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return ""; // Empty - wrapper div handles styling
    } else {
      return "bg-white border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm";
    }
  }, [context]);

  const inputClass = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "mb-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
    } else {
      return "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
    }
  }, [context]);

  const headerSize = useMemo(() => {
    if (context === 'ivrMenu' || context === 'offHoursIvrMenu') {
      return "font-semibold text-[15px]"; // Match ForwardUI styling for consistency
    } else {
      return "font-bold text-[18px]";
    }
  }, [context]);

  const showDivider = context === 'ivrMenu' || context === 'offHoursIvrMenu';
  const useIVRStyle = context === 'ivrMenu' || context === 'offHoursIvrMenu';

  return {
    recordingValue,
    handleRecordingChange,
    containerClass,
    inputClass,
    headerSize,
    showDivider,
    useIVRStyle,
  };
};

/**
 * Shared hook for PlayRecording action component logic across all contexts
 */
export const usePlayRecordingAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  // Get recording value based on context
  const recordingValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle recording change
  const handleRecordingChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryRecording',
      offHours: 'offHoursRecording',
      root: 'primaryRecording',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  return {
    recordingValue,
    handleRecordingChange,
    ...styling,
  };
};

/**
 * Shared hook for ElevenLabs action component logic across all contexts
 */
export const useElevenLabsAction = (context, route, setRoute, ivrOptionId = null, optionKey = null) => {
  const [imgError, setImgError] = useState(false);

  // Get agent ID value based on context
  const agentIdValue = useMemo(() => {
    return getValueFromRoute(context, route, ivrOptionId, {
      ivr: 'primaryAgent',
      offHours: 'offHoursAgent',
      root: 'primaryAgent',
    }, optionKey);
  }, [context, route, ivrOptionId, optionKey]);

  // Handle agent ID change
  const handleAgentIdChange = useCallback((value) => {
    updateRouteValue(context, setRoute, ivrOptionId, {
      ivr: 'primaryAgent',
      offHours: 'offHoursAgent',
      root: 'primaryAgent',
    }, value, {}, optionKey);
  }, [context, setRoute, ivrOptionId, optionKey]);

  const styling = useMemo(() => getCommonStylingValues(context), [context]);

  return {
    agentIdValue,
    handleAgentIdChange,
    imgError,
    setImgError,
    ...styling,
  };
};

/**
 * Builds agent options for ElevenLabs dropdown from org channels.
 * Enabled agents first (from agents.L), disabled agents last (from el_exclusion_list) with title on hover.
 */
export const useElevenLabsAgentOptions = (channels) => {
  return useMemo(() => {
    const byId = new Map();
    (channels || [])
      .filter((ch) => ch.M?.type?.S === "elevenlabs")
      .forEach((ch) => {
        const channelLabel = ch.M?.label?.S || "ElevenLabs";
        (ch.M?.agents?.L || []).forEach((item) => {
          const id = item.M?.id?.S ?? item.M?.agent_id?.S;
          const name = item.M?.name?.S ?? id ?? "Unknown";
          if (id && !byId.has(id)) {
            byId.set(id, { value: id, label: `${name} (${channelLabel})`, disabled: false });
          }
        });
        (ch.M?.el_exclusion_list?.L || []).forEach((item) => {
          const id = item.S ?? item.M?.id?.S;
          if (id && !byId.has(id)) {
            byId.set(id, {
              value: id,
              label: `${id} (disabled)`,
              disabled: true,
              title: "This agent is disabled in the channel settings. Enable it in Channels to use it.",
            });
          }
        });
      });
    const enabled = [...byId.values()].filter((o) => !o.disabled);
    const disabled = [...byId.values()].filter((o) => o.disabled);
    return [...enabled, ...disabled];
  }, [channels]);
};
